import { Box, SimpleGrid, Stack, Group, Progress, RingProgress, Table } from '@mantine/core';
import {
  IconCurrencyDollar,
  IconReceipt,
  IconClock,
  IconAlertTriangle,
  IconTrendingUp,
  IconArrowRight,
} from '@tabler/icons-react';
import { useNavigate } from 'react-router-dom';
import { Card, Text, Title, Button } from '../../../../components/ui';
import {
  invoicingStats,
  invoicesByStatus,
  revenueByMonth,
  mockInvoices,
  mockPayments,
} from '../mockData/invoicing';

const formatCurrency = (value: number) => {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(value);
};

const StatCard = ({
  title,
  value,
  change,
  icon: Icon,
  color
}: {
  title: string;
  value: string;
  change?: number;
  icon: React.ElementType;
  color: string;
}) => (
  <Card>
    <Group justify="space-between" mb="xs">
      <Text size="sm" c="dimmed">{title}</Text>
      <Box
        style={{
          width: 40,
          height: 40,
          borderRadius: 8,
          backgroundColor: `var(--mantine-color-${color}-light)`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Icon size={20} color={`var(--mantine-color-${color}-filled)`} />
      </Box>
    </Group>
    <Text size="xl" fw={700}>{value}</Text>
    {change !== undefined && (
      <Group gap="xs" mt="xs">
        <IconTrendingUp size={16} color="var(--mantine-color-green-filled)" />
        <Text size="xs" c="green">+{change}%</Text>
        <Text size="xs" c="dimmed">vs last month</Text>
      </Group>
    )}
  </Card>
);

const formatDate = (date: Date) => {
  return new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric' }).format(date);
};

export default function InvoicingDashboardPage() {
  const navigate = useNavigate();
  const recentInvoices = mockInvoices.slice(0, 5);
  const recentPayments = mockPayments.filter(p => p.status === 'completed').slice(0, 3);

  const totalByStatus = invoicesByStatus.reduce((acc, s) => acc + s.amount, 0);

  return (
    <Stack gap="lg">
      {/* Header */}
      <Group justify="space-between">
        <Box>
          <Title order={2}>Invoicing Dashboard</Title>
          <Text c="dimmed" size="sm">Manage your invoices and payments</Text>
        </Box>
        <Group>
          <Button variant="light" onClick={() => navigate('invoices')}>View Invoices</Button>
          <Button onClick={() => navigate('invoices')}>Create Invoice</Button>
        </Group>
      </Group>

      {/* Stats Grid */}
      <SimpleGrid cols={{ base: 1, sm: 2, lg: 4 }}>
        <StatCard
          title="Total Revenue"
          value={formatCurrency(invoicingStats.totalRevenue)}
          change={invoicingStats.revenueChange}
          icon={IconCurrencyDollar}
          color="green"
        />
        <StatCard
          title="Paid Invoices"
          value={formatCurrency(invoicingStats.paidAmount)}
          icon={IconReceipt}
          color="blue"
        />
        <StatCard
          title="Pending"
          value={formatCurrency(invoicingStats.pendingAmount)}
          icon={IconClock}
          color="orange"
        />
        <StatCard
          title="Overdue"
          value={formatCurrency(invoicingStats.overdueAmount)}
          icon={IconAlertTriangle}
          color="red"
        />
      </SimpleGrid>

      {/* Revenue Chart and Status Breakdown */}
      <SimpleGrid cols={{ base: 1, lg: 2 }}>
        {/* Revenue Trend */}
        <Card>
          <Text fw={600} mb="md">Revenue Trend</Text>
          <Stack gap="sm">
            {revenueByMonth.map(month => {
              const maxRevenue = Math.max(...revenueByMonth.map(m => m.revenue));
              return (
                <Group key={month.month} justify="space-between">
                  <Text size="sm" w={40}>{month.month}</Text>
                  <Progress
                    value={(month.revenue / maxRevenue) * 100}
                    size="lg"
                    radius="xl"
                    style={{ flex: 1 }}
                    color="blue"
                  />
                  <Text size="sm" fw={500} w={100} ta="right">{formatCurrency(month.revenue)}</Text>
                </Group>
              );
            })}
          </Stack>
        </Card>

        {/* Invoice Status */}
        <Card>
          <Text fw={600} mb="md">Invoice Status</Text>
          <Group align="center" gap="xl">
            <RingProgress
              size={140}
              thickness={16}
              sections={invoicesByStatus
                .filter(s => s.amount > 0)
                .map(s => ({
                  value: (s.amount / totalByStatus) * 100,
                  color: s.color,
                }))}
              label={
                <Box ta="center">
                  <Text size="lg" fw={700}>{mockInvoices.length}</Text>
                  <Text size="xs" c="dimmed">Invoices</Text>
                </Box>
              }
            />
            <Stack gap="xs" style={{ flex: 1 }}>
              {invoicesByStatus.filter(s => s.count > 0).map(status => (
                <Group key={status.status} justify="space-between">
                  <Group gap="sm">
                    <Box
                      w={12}
                      h={12}
                      style={{ borderRadius: 4, backgroundColor: `var(--mantine-color-${status.color}-filled)` }}
                    />
                    <Text size="sm">{status.status}</Text>
                  </Group>
                  <Group gap="md">
                    <Text size="sm" c="dimmed">{status.count}</Text>
                    <Text size="sm" fw={500}>{formatCurrency(status.amount)}</Text>
                  </Group>
                </Group>
              ))}
            </Stack>
          </Group>
        </Card>
      </SimpleGrid>

      {/* Recent Invoices and Payments */}
      <SimpleGrid cols={{ base: 1, lg: 2 }}>
        {/* Recent Invoices */}
        <Card>
          <Group justify="space-between" mb="md">
            <Text fw={600}>Recent Invoices</Text>
            <Button
              variant="subtle"
              size="xs"
              rightSection={<IconArrowRight size={14} />}
              onClick={() => navigate('invoices')}
            >
              View All
            </Button>
          </Group>
          <Table>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Invoice</Table.Th>
                <Table.Th>Client</Table.Th>
                <Table.Th ta="right">Amount</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {recentInvoices.map(invoice => (
                <Table.Tr key={invoice.id}>
                  <Table.Td>
                    <Text size="sm" fw={500}>{invoice.invoiceNumber}</Text>
                  </Table.Td>
                  <Table.Td>
                    <Text size="sm" c="dimmed">{invoice.clientName}</Text>
                  </Table.Td>
                  <Table.Td ta="right">
                    <Text size="sm" fw={500}>{formatCurrency(invoice.total)}</Text>
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </Card>

        {/* Recent Payments */}
        <Card>
          <Group justify="space-between" mb="md">
            <Text fw={600}>Recent Payments</Text>
            <Button
              variant="subtle"
              size="xs"
              rightSection={<IconArrowRight size={14} />}
              onClick={() => navigate('payments')}
            >
              View All
            </Button>
          </Group>
          <Stack gap="sm">
            {recentPayments.map(payment => (
              <Group key={payment.id} justify="space-between" p="sm" style={{ backgroundColor: 'var(--mantine-color-gray-light)', borderRadius: 8 }}>
                <Box>
                  <Text size="sm" fw={500}>{payment.clientName}</Text>
                  <Text size="xs" c="dimmed">{payment.invoiceNumber}</Text>
                </Box>
                <Box ta="right">
                  <Text size="sm" fw={600} c="green">{formatCurrency(payment.amount)}</Text>
                  <Text size="xs" c="dimmed">{formatDate(payment.processedAt)}</Text>
                </Box>
              </Group>
            ))}
          </Stack>
        </Card>
      </SimpleGrid>
    </Stack>
  );
}
